import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import ModelService from "src/services/modelService";
import { DUMMY_MODEL_ID } from "src/constants";
import { LOADING_STATE } from "src/types/constants";
import SiteSerivce from "src/services/siteSerivce";
import { modelFieldOptionSort } from "src/utils/stringUtil";

export interface FieldExample {
  input: string;
  output: string[];
}

export const MODEL_DEFAULT_PERMISSION = "default";

export enum FieldType {
  selection = "selection",
  freeText = "free_text",
}

export enum ModelStatus {
  active = "Active",
  inactive = "Inactive",
}

export interface OptionConfig {
  metadataKey: string;
  action: string;
}

export interface Field {
  id: string;
  name: string;
  description: string;
  type: FieldType | string;
  many: boolean;
  examples: FieldExample[];
  options: string[];
  optionConfig?: OptionConfig;
  overrideOptions?: string[];
}

export interface LabelHierarchy {
  parentFieldName: string;
  childFieldName: string;
  options: Record<string, string[]>;
}

export interface Model {
  id: string;
  name: string;
  fields: Record<string, Field>;
  hierarchy?: LabelHierarchy[];
  status?: ModelStatus;
  isRagEnabled?: boolean;
  requiredReviewerCount?: number;
  ownerAlias?: string;
  permissions?: Record<string, Set<string>>;
  batchProcessLimit?: number;
  metadata?: Record<string, any>;
}

export interface ModelView {
  id: string;
  name: string;
  numberOfLabels: number;
  ownerAlias: string;
  permissions?: Record<string, Set<string>>;
}

export type ModelManagementPageState = {
  models: Record<string, Model>;
  selectedModelId: string;
  selectedFieldId: string;
  selectedModel: Model;
  modelView: ModelView[];
  errors: Record<string, string>;
  isCreatingField: boolean;
  isCreateModel: boolean;
  getModelByIdLoading: LOADING_STATE;
  getSiteDetailsLoading: LOADING_STATE;
};

const InitialModel: Model = {
  id: "",
  name: "",
  fields: {},
  hierarchy: [],
  status: ModelStatus.inactive,
  isRagEnabled: false,
  requiredReviewerCount: 0,
  ownerAlias: "",
  permissions: {},
  batchProcessLimit: 0,
  metadata: {},
};

export const initialField: Field = {
  id: "",
  name: "",
  description: "",
  type: "",
  examples: [],
  options: [],
  many: false,
};

export const MODEL_NAME_EMPTY = "Model name cannot be empty";

export const MODEL_NO_FIELD = "Model must have at least one field";

export const FIELD_NAME_EMPTY = "Field name cannot be empty";

export const FIELD_DESCRIPTION_EMPTY = "Description cannot be empty";

export const FIELD_TYPE_EMPTY = "Field must has one type";

export const FIELD_OPTION_EMPTY = "Field option cannot be empty";

export const FIELD_EXAMPLE_INPUT_EMPTY = "Example input cannot be empty";

export const FIELD_EXAMPLE_INPUT_TOO_LONG =
  "Example input length cannot longer than 300 characters";

export const FIELD_EXAMPLE_OUTPUT_EMPTY = "Example output cannot be empty";

export const EXAMPLE_OPTION_INVALID = "Example output must be one of the options";

export const getModelNameKey = (modelId: string) => {
  return modelId + "_NAME";
};

export const getModelFieldKey = (modelId: string) => {
  return modelId + "_FIELD";
};

export const getFieldNameKey = (fieldId: string) => {
  return fieldId + "_NAME";
};

export const getFieldTypeKey = (fieldId: string) => {
  return fieldId + "_Type";
};

export const getFieldDescriptionKey = (fieldId: string) => {
  return fieldId + "_DESCRIPTION";
};

export const getFieldOptionsKey = (fieldId: string) => {
  return fieldId + "_OPTIONS";
};

export const getFieldExampleKey = (fieldId: string, index: number, type: string) => {
  return fieldId + "_EXAMPLE_" + index + type;
};

export const getModels = createAsyncThunk("modelManagement/getModels", async () => {
  const { data } = await ModelService.getModels();
  return data;
});

export const getModelById = createAsyncThunk(
  "modelManagement/getModelById",
  async (modelId: string) => {
    const { data } = await ModelService.getModelById(modelId);
    return data;
  },
);

export const getSiteDetails = createAsyncThunk(
  "modelManagement/getSiteDetails",
  async (siteCode: string) => {
    const { data } = await SiteSerivce.getSiteDetails(siteCode);
    return data;
  },
);

export const getSiteDetailsV2 = createAsyncThunk(
  "modelManagement/getSiteDetailsV2",
  async (siteCode: string) => {
    const { data } = await SiteSerivce.getSiteDetailsV2(siteCode);
    return data;
  },
);

export const validateModel = (model: Model) => {
  const errors: Record<string, string> = {};
  if (!model.name) {
    errors[getModelNameKey(model.id)] = MODEL_NAME_EMPTY;
  }
  if (Object.entries(model.fields).length === 0) {
    errors[getModelFieldKey(model.id)] = MODEL_NO_FIELD;
  }
  Object.values(model.fields).forEach((field: Field) => {
    if (!field.name) {
      errors[getFieldNameKey(field.id)] = FIELD_NAME_EMPTY;
    }
    if (!field.description) {
      errors[getFieldDescriptionKey(field.id)] = FIELD_DESCRIPTION_EMPTY;
    }
    if (!field.type) {
      errors[getFieldTypeKey(field.id)] = FIELD_TYPE_EMPTY;
    }
    if (field.type === FieldType.selection && field.options.length === 0) {
      errors[getFieldOptionsKey(field.id)] = FIELD_OPTION_EMPTY;
    }
    field.examples.forEach((example, index) => {
      if (!example.input) {
        errors[getFieldExampleKey(field.id, index, "INPUT")] = FIELD_EXAMPLE_INPUT_EMPTY;
      } else if (example.input.length > 3000) {
        errors[getFieldExampleKey(field.id, index, "INPUT")] = FIELD_EXAMPLE_INPUT_TOO_LONG;
      }
      if (!example.output || example.output.length === 0 || example.output.every((o) => !o)) {
        errors[getFieldExampleKey(field.id, index, "OUTPUT")] = FIELD_EXAMPLE_OUTPUT_EMPTY;
      }
    });
  });
  return errors;
};

export const validateAndUpsertModel = createAsyncThunk<
  any,
  Model,
  { rejectValue: Record<string, string> }
>("modelManagement/upsertModel", async (model, { rejectWithValue }) => {
  const errors = validateModel(model);
  if (Object.keys(errors).length > 0) {
    return rejectWithValue(errors);
  }
  const { data } = await ModelService.upsertModel(toModelPayload(model));
  return data;
});

export const toLabelPayload = (field: Field) => {
  return {
    ...field,
    options: Array.from(new Set(field.options.map((option) => option.trim()))),
    examples: field.examples.map((example) => {
      return {
        input: example.input,
        output: field.many
          ? example.output.filter((option: string) => option !== "")
          : example.output[0],
      };
    }),
  };
};

export const toModelPayload = (model: Model) => {
  // Convert permissions from Set to array if they exist
  const processedPermissions = model.permissions
    ? Object.entries(model.permissions).reduce(
        (acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value : Array.from(value || []);
          return acc;
        },
        {} as Record<string, string[]>,
      )
    : null;
  if (model.id === DUMMY_MODEL_ID) {
    return {
      id: undefined,
      name: model.name,
      labels: Object.keys(model.fields).map((id) => toLabelPayload(model.fields[id])),
      permissions: processedPermissions,
      metadata: model.metadata,
      hierarchy: model.hierarchy,
    };
  } else {
    return {
      id: model.id,
      name: model.name,
      labels: Object.keys(model.fields).map((id) => {
        return toLabelPayload(model.fields[id]);
      }),
      permissions: processedPermissions,
      batchProcessLimit: model.batchProcessLimit,
      metadata: model.metadata,
      hierarchy: model.hierarchy,
    };
  }
};

export const toModelJsonPayload = (model: Model) => {
  return {
    labels: Object.keys(model.fields).map((id) => {
      const label = toLabelPayload(model.fields[id]);
      const { id: _, ...rawlabel } = label;
      return rawlabel;
    }),
    hierarchy: model.hierarchy || [],
  };
};

const initialState: ModelManagementPageState = {
  models: {},
  selectedModelId: "",
  selectedFieldId: "",
  selectedModel: InitialModel,
  modelView: [],
  errors: {},
  isCreatingField: false,
  isCreateModel: false,
  getModelByIdLoading: "idle",
  getSiteDetailsLoading: "idle",
};

/** Job Config Page Slice */
const { reducer, actions } = createSlice({
  name: "modelConfigPageState",
  initialState,
  reducers: {
    setSelectedModelId: (state, action) => {
      state.selectedModelId = action.payload;
    },
    setSelectedFieldId: (state, action) => {
      state.selectedFieldId = action.payload;
    },
    setModelName: (state, action) => {
      state.selectedModel.name = action.payload;
    },
    setModel: (state, action) => {
      state.selectedModel = action.payload;
    },
    updateField: (state, action) => {
      state.selectedModel.fields[action.payload.id] = action.payload;
    },
    updateModelPermission: (state, action) => {
      state.selectedModel.permissions = {
        [MODEL_DEFAULT_PERMISSION]: action.payload
          .split(";")
          .filter((alias: string) => alias.trim() !== ""),
      };
    },
    setFieldName: (state, action) => {
      state.selectedModel.fields[state.selectedFieldId].name = action.payload;
    },
    setFieldDescription: (state, action) => {
      state.selectedModel.fields[state.selectedFieldId].description = action.payload;
    },
    setFieldType: (state, action) => {
      state.selectedModel.fields[state.selectedFieldId].type = action.payload;
    },
    setFieldOptions: (state, action) => {
      state.selectedModel.fields[state.selectedFieldId].options = action.payload.split("\n");
    },
    setFieldExamples: (state, action) => {
      const index = action.payload.index;
      if (action.payload.input !== undefined) {
        state.selectedModel.fields[state.selectedFieldId].examples[index].input =
          action.payload.input;
      }
      if (action.payload.output !== undefined) {
        if (state.selectedModel.fields[state.selectedFieldId].many) {
          action.payload.output.filter((option: string) => option !== "");
          state.selectedModel.fields[state.selectedFieldId].examples[index].output =
            action.payload.output;
        } else {
          if (!state.selectedModel.fields[state.selectedFieldId].examples[index].output) {
            state.selectedModel.fields[state.selectedFieldId].examples[index].output = [];
          }
          state.selectedModel.fields[state.selectedFieldId].examples[index].output[0] =
            action.payload.output;
        }
      }
    },
    setFieldMany: (state, action) => {
      if (action.payload === undefined) {
        // this is for existed data don't have many field
        state.selectedModel.fields[state.selectedFieldId].many = true;
      } else {
        state.selectedModel.fields[state.selectedFieldId].many = action.payload;
      }
    },
    addFieldExample: (state) => {
      state.selectedModel.fields[state.selectedFieldId].examples.push({
        input: "",
        output: [],
      });
    },
    removeFieldExample: (state, action) => {
      state.selectedModel.fields[state.selectedFieldId].examples.splice(action.payload, 1);
    },
    addNewField: (state) => {
      const newField = {
        id: uuidv4(),
        name: "",
        description: "",
        options: [],
        type: "",
        many: false,
        examples: [{ input: "", output: [] }],
        optionConfig: undefined,
      };
      state.selectedModel.fields[newField.id] = newField;
      state.selectedFieldId = newField.id;
      state.isCreatingField = true;
    },
    addNewModel: (state) => {
      state.selectedModelId = DUMMY_MODEL_ID;
      state.selectedModel = {
        id: DUMMY_MODEL_ID,
        name: "",
        fields: {},
        hierarchy: [],
        status: ModelStatus.active,
        isRagEnabled: undefined,
        requiredReviewerCount: undefined,
        ownerAlias: undefined,
        metadata: {},
      };
      state.selectedFieldId = "";
      state.isCreateModel = true;
      state.isCreatingField = false;
    },
    resetField: (state) => {
      state.selectedFieldId = "";
      state.isCreatingField = false;
    },
    removeField: (state) => {
      delete state.selectedModel.fields[state.selectedFieldId];
      state.selectedFieldId = "";
      state.isCreateModel = false;
      state.isCreatingField = false;
    },
    resetGetSiteDetailsLoading: (state) => {
      state.getSiteDetailsLoading = "idle";
    },
    setBatchProcessLimit: (state, action) => {
      state.selectedModel.batchProcessLimit = action.payload;
    },
    setMetadata: (state, action) => {
      state.selectedModel.metadata = action.payload;
    },
    setMetadataField: (state, action) => {
      const { key, value } = action.payload;
      if (!state.selectedModel.metadata) {
        state.selectedModel.metadata = {};
      }
      if (value === null || value === undefined || value === "") {
        delete state.selectedModel.metadata[key];
      } else {
        state.selectedModel.metadata[key] = value;
      }
    },
    removeMetadataField: (state, action) => {
      const key = action.payload;
      if (state.selectedModel.metadata && state.selectedModel.metadata[key]) {
        delete state.selectedModel.metadata[key];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getModels.fulfilled, (state, { payload }) => {
      state.modelView = payload.sort((a: ModelView, b: ModelView) => a.name.localeCompare(b.name));
    });
    builder.addCase(getModels.pending, (state, { payload }) => {
      state.getModelByIdLoading = "pending";
    });
    builder.addCase(getModelById.fulfilled, (state, { payload }) => {
      const fieldMap: Record<string, Field> = {};
      payload.labels.forEach((field: any, index: number) => {
        const tmpId = payload.id + field.name + index;
        modelFieldOptionSort(field.options);
        field.examples.forEach((example: any) => {
          if (!field.many && typeof example.output === "string") {
            example.output = [example.output];
          }
        });
        fieldMap[tmpId] = { id: tmpId, ...field };
      });
      state.models[payload.id] = { ...payload, fields: fieldMap, metadata: payload.metadata || {} };
      state.selectedModelId = payload.id;
      state.selectedModel = {
        ...payload,
        fields: fieldMap,
        metadata: payload.metadata || {},
      };
      state.getModelByIdLoading = "fulfilled";
    });
    builder.addCase(validateAndUpsertModel.pending, (state) => {
      state.errors = {};
    });
    builder.addCase(validateAndUpsertModel.rejected, (state, { payload }) => {
      if (payload) {
        state.errors = payload;
      }
    });
    builder.addCase(validateAndUpsertModel.fulfilled, (state, { payload }) => {
      state.errors = {};

      const fieldMap: Record<string, Field> = {};
      // Safely access selectedFieldName only if selectedFieldId exists and the field exists
      const selectedFieldName =
        state.selectedFieldId && state.selectedModel.fields[state.selectedFieldId]
          ? state.selectedModel.fields[state.selectedFieldId].name
          : undefined;

      payload.labels.forEach((field: any, index: number) => {
        const tmpId = payload.id + field.name + index;
        // Only update selectedFieldId if we had a selectedFieldName and it matches
        if (selectedFieldName && field.name === selectedFieldName) {
          state.selectedFieldId = tmpId;
        }
        modelFieldOptionSort(field.options);
        field.examples.forEach((example: any) => {
          if (!field.many && typeof example.output === "string") {
            example.output = [example.output];
          }
        });

        fieldMap[tmpId] = { id: tmpId, ...field };
      });
      state.models[payload.id] = { ...payload, fields: fieldMap, metadata: payload.metadata || {} };
      state.selectedModelId = payload.id;
      state.selectedModel = {
        ...payload,
        fields: fieldMap,
        metadata: payload.metadata || {},
      };
      state.isCreateModel = false;
      state.isCreatingField = false;
    });
    builder.addCase(getSiteDetails.fulfilled, (state, { payload }) => {
      const siteLocations: string[] = Array.from(new Set(payload.location));
      const siteProcessPath: string[] = Array.from(new Set(payload.process_path));

      // Only update fields that have site_location_mapping action
      Object.entries(state.selectedModel.fields).forEach(([key, field]) => {
        if (field.optionConfig?.action === "site_location_mapping" && siteLocations.length !== 0) {
          state.selectedModel.fields[key].overrideOptions = siteLocations;
        }
        if (
          field.optionConfig?.action === "site_process_path_mapping" &&
          siteLocations.length !== 0
        ) {
          state.selectedModel.fields[key].overrideOptions = siteProcessPath;
        }
      });
      state.getSiteDetailsLoading = "fulfilled";
    });
    builder.addCase(getSiteDetailsV2.fulfilled, (state, { payload }) => {
      const siteLocations: string[] = Array.from(new Set(payload.location));
      const siteProcessPath: string[] = Array.from(new Set(payload.process_path));

      // Only update fields that have site_location_mapping_v2 action
      Object.entries(state.selectedModel.fields).forEach(([key, field]) => {
        if (
          field.optionConfig?.action === "site_location_mapping_v2" &&
          siteLocations.length !== 0
        ) {
          state.selectedModel.fields[key].overrideOptions = siteLocations;
        }
        if (
          field.optionConfig?.action === "site_process_path_mapping" &&
          siteLocations.length !== 0
        ) {
          state.selectedModel.fields[key].overrideOptions = siteProcessPath;
        }
      });
      state.getSiteDetailsLoading = "fulfilled";
    });
  },
});

/**
 * Convert CSV data to hierarchy JSON format
 * @param csvData Array of objects with parentFieldName, childFieldName, parentOption, childOption
 * @returns Formatted hierarchy data for API
 */
export const convertCsvToHierarchyJson = (csvData: any[]): LabelHierarchy[] => {
  // Group data by parent-child field pairs
  const groupedData: Record<string, any[]> = {};

  csvData.forEach((row) => {
    const key = `${row.parentFieldName}:${row.childFieldName}`;
    if (!groupedData[key]) {
      groupedData[key] = [];
    }
    groupedData[key].push(row);
  });

  // Convert grouped data to hierarchy format
  const result: LabelHierarchy[] = Object.entries(groupedData).map(([key, rows]) => {
    const [parentFieldName, childFieldName] = key.split(":");

    // Group by parent option
    const options: Record<string, string[]> = {};
    rows.forEach((row) => {
      if (!options[row.parentOption]) {
        options[row.parentOption] = [];
      }
      if (row.childOption && !options[row.parentOption].includes(row.childOption)) {
        options[row.parentOption].push(row.childOption);
      }
    });

    return {
      parentFieldName,
      childFieldName,
      options,
    };
  });

  return result;
};

// Note: Hierarchies should be updated using validateAndUpsertModel with the complete model

export const downloadJSON = createAsyncThunk(
  "modelManagement/downloadJSON",
  async (model: Model) => {
    const modelPayload = toModelJsonPayload(model);
    const modelData = JSON.stringify(modelPayload, null, 2);
    const blob = new Blob([modelData], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const { downloadFile } = await import("src/utils/fileUtil");
    downloadFile(url, `${model.name || "model"}.json`);
    return;
  },
);

export const importModelFromJSON = createAsyncThunk(
  "modelManagement/importModelFromJSON",
  async (file: File, { dispatch }) => {
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const jsonContent = JSON.parse(e.target?.result as string);
          if (!jsonContent.labels || !Array.isArray(jsonContent.labels)) {
            throw new Error("Invalid model JSON format");
          }

          // Get the current model name from state
          const state = (await import("src/store/store")).default.getState();
          const currentModelName = state.modelSlice.selectedModel.name;

          const newModel: Model = {
            id: DUMMY_MODEL_ID,
            name: currentModelName || "", // Use existing model name if available
            fields: {},
            hierarchy: jsonContent.hierarchy || [],
            status: ModelStatus.active,
            isRagEnabled: undefined,
            requiredReviewerCount: undefined,
            ownerAlias: undefined,
          };

          // Convert labels to fields
          jsonContent.labels.forEach((label: any, index: number) => {
            const fieldId = uuidv4();
            newModel.fields[fieldId] = {
              id: fieldId,
              name: label.name || `Field ${index + 1}`,
              description: label.description || "",
              type: label.type || FieldType.freeText,
              many: label.many || false,
              options: label.options || [],
              examples:
                label.examples?.map((ex: any) => ({
                  input: ex.input || "",
                  output: Array.isArray(ex.output) ? ex.output : [ex.output || ""],
                })) || [],
            };
          });

          dispatch(addNewModel());
          dispatch(setModel(newModel));
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  },
);

export const {
  setSelectedModelId,
  setSelectedFieldId,
  setModelName,
  setFieldName,
  setFieldType,
  setFieldDescription,
  setFieldOptions,
  setFieldExamples,
  addFieldExample,
  removeFieldExample,
  addNewField,
  addNewModel,
  updateModelPermission,
  resetField,
  removeField,
  setFieldMany,
  resetGetSiteDetailsLoading,
  setModel,
  setBatchProcessLimit,
  setMetadata,
  setMetadataField,
  removeMetadataField,
} = actions;

export default reducer;
